import type { PhaseThreeDto, WizardStepDto } from '@bk/contracts';
import { useTenant } from '../../lib/useTenant';
import { Figures, talkAbout } from './Fase';
import { PhaseFrame, usePhase } from './fasi-comune';

/**
 * La fase 3 «Le attività» (docs/mockup/percorso-sinapsi/06-fase3; contratto n. 1 versione 2.0, art. 8, fase 3; ADR
 * 0174): cosa fa l'azienda, dove lo fa e per chi; i codici non esercitati «da confermare»; i servizi online; lo scarto.
 * Nessuna fase del processo: è la fase 4.
 */
export function Fase3Page({ next, onNext, sealing, stopped = false }: { next: WizardStepDto | null; onNext: () => void; sealing: boolean; stopped?: boolean }) {
  const { q, reload, confirm, redo } = usePhase<PhaseThreeDto>(3);
  const { isManager } = useTenant();
  return (
    <PhaseFrame
      d={q.data}
      error={q.error}
      eyebrow="Fase 3 · Le attività"
      title={(d) => `Cosa fa ${d.azienda ?? 'la tua azienda'}, dove e per chi`}
      working="Preparo le attività"
      questionsIntro="Solo ciò che le fonti non dicono."
      scrapIntro="Le attività esercitate senza un codice registrato e i codici registrati non esercitati."
      next={next}
      onNext={onNext}
      sealing={sealing}
      stopped={stopped}
      onConfirm={confirm}
      onRedo={redo}
      reload={reload}
      afterQuestions={(d) => (
        <>
          {d.non_chiesto && <p className="sn-note">{d.non_chiesto}</p>}
          {isManager && (
            <div>
              <button type="button" className="sn-btn ghost sm" style={{ fontSize: 13 }} onClick={() => talkAbout('Facciamo anche questa attività: ')}>
                Aggiungi un’attività che manca
              </button>
            </div>
          )}
        </>
      )}
    >
      {(d) => (
        <>
          <Figures list={d.numeri} />
          <section className="sn-panel flush" style={{ gap: 0 }}>
            <table className="sn-tbl wide">
              <thead>
                <tr>
                  <th>Attività · nome del mestiere e dell’azienda</th>
                  <th style={{ width: 90 }}>Codice</th>
                  <th style={{ width: 190 }}>Dove si svolge</th>
                  <th style={{ width: 120 }}>Stato</th>
                  <th style={{ width: 140 }}>Fonte</th>
                </tr>
              </thead>
              <tbody>
                {d.attivita.map((a) => (
                  <tr key={a.id}>
                    <td>
                      <b>{a.nome_standard}</b>
                      {a.prevalente && <span className="sn-src"> · prevalente</span>}
                      {a.grado && a.grado !== 'certo' && <span className="sn-src gold"> · {a.grado}</span>}
                      {a.nome_azienda && <span className="sub">L’azienda la chiama «{a.nome_azienda}»</span>}
                      {a.per_chi.length > 0 && <span className="sub">Per: {a.per_chi.map((p) => (p === 'pubblica_amministrazione' ? 'pubblica amministrazione' : p)).join(', ')}</span>}
                      {a.peso_ricavi && <span className="sub">Peso sui ricavi: {a.peso_ricavi}</span>}
                    </td>
                    <td className="v mute" style={{ fontFamily: a.codice ? 'var(--sn-mono)' : undefined, color: a.codice ? '#eaf0ff' : undefined }}>
                      {a.codice ?? 'senza codice'}
                    </td>
                    <td className={`v ${/confermare/.test(a.dove) ? 'gold' : ''}`}>{a.dove}</td>
                    <td>
                      <span className={`sn-src ${a.stato === 'esercitata' ? 'ok' : 'gold'}`}>{a.stato === 'esercitata' ? 'Esercitata' : a.stato === 'non_esercitata' ? 'Non esercitata' : 'Non esercitata, da confermare'}</span>
                    </td>
                    <td className="f on">{a.fonti.join(', ')}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </section>
          <section className="sn-panel" style={{ gap: 10, padding: '20px 24px' }}>
            <div className="sn-ph">
              <h2 className="sn-h2">I servizi online dell’azienda</h2>
              <span className="lbl">Pagine verificate</span>
            </div>
            <div className="sn-svc">
              {d.servizi_online.map((x) => (
                <span key={x.indirizzo_web} style={{ display: 'contents' }}>
                  <span className="sn-dot violet" style={{ margin: '0 auto' }} />
                  <span>
                    <a href={/^https?:/.test(x.indirizzo_web) ? x.indirizzo_web : `https://${x.indirizzo_web}`} target="_blank" rel="noreferrer" style={{ fontWeight: 700 }}>
                      {x.indirizzo_web.replace(/^https?:\/\/(www\.)?/, '').replace(/\/$/, '')}
                    </a>{' '}
                    <span className="sn-mute">· {x.testo}</span> <span className="sn-src violet">· {x.fonte}</span>
                  </span>
                  <span className="what">{x.cosa_fa}</span>
                </span>
              ))}
              {d.servizi_nota && (
                <>
                  <span className="sn-dot off" style={{ margin: '0 auto' }} />
                  <span className="sn-mute">{d.servizi_nota}</span>
                  <span className="what sn-mute">{d.servizi_online.length ? '' : 'Non trovato'}</span>
                </>
              )}
            </div>
          </section>
        </>
      )}
    </PhaseFrame>
  );
}
